/**
 * @file services/index.ts
 * @author zhoutingshuai <zhoutingshuai@baidu.com>
 */
import {
    ADDITIONAL_INFO,
    AUTH_CHECK_API,
    BENEFITSCARDGAIN_API,
    CHARACTER_INFO,
    CHAT_RECOVER_API,
    CHAT_SHARE_CREATE,
    COIN_DAY_PAY_COUNTDOWN_API,
    COIN_GUIDE_STATUS,
    COIN_TASK_ADD_API,
    COIN_TOTAL_API,
    COIN_VIP_REFUND_API,
    COLLECT_CHARACTER_API,
    CONSUME_API,
    DELETE_BOT_API,
    DELETE_MESSAGE_API,
    ERROR_NET,
    EXPLORE_WORLD_SUG_API,
    FEEDBACK_API,
    FEEDBACK_SUBMIT_API,
    FEEDBACKINFO_API,
    FREE_SUBS_CARD,
    FREE_SUBSCRIBE_UPDATE,
    GENERATE_DOCUMENT_API,
    GET_COUPON_CODE,
    GET_LANGUAGE,
    GET_MUSIC_LINK,
    HISTORY_API,
    INPUT_BOX_GUIDE_API,
    OFF_WORK_REPORT_API,
    PayCardType,
    POST_CLUE_API,
    PROMPT_INFO,
    QUESTION_API,
    REPORT_VISIT_AGENT,
    SHAKE_API,
    SHORTENURL_API,
    SMS_CODE_API,
    STOP_ANSWER_API,
    SUGGESTIONS_API,
    TOP_BAR_API,
    UPDATE_WELCOME_HISTORY_API,
    UPLOAD_API,
    WELCOME_ASYNC_API,
    WELCOME_CONSUMED_API,
    WELCOME_IN_HISTORY_API,
} from '@lib/constants';
import {showNaToast} from '@src/lib/utils/native';
import {
    httpGet,
    httpPost,
} from '@src/lib/utils/request';
import {buildParam} from '@src/lib/utils/tools';
// import {historyData} from '../../../../mock/history1';
import {
    AnsResponse,
    DeleteMessageData,
    FreeSubsCardRes,
    HistoryResponse,
    WelcomeCardType,
} from '../types';

interface LikeResponse {
    data: {
        [key: string]: string;
    };
    errmsg: string;
    errno: number;
    logid: string;
}

interface LikeParams {
    messageId?: string;
    isLike: number;
    type?: string;
    wId?: number;
}

interface CoinTotalRes {
    data: {
        totalCoin: number;
        showNewGuide: boolean;
    };
    errmsg: string;
    errno: number;
    logid: string;
}

interface CoinDayPay {
    data: {
        expires: number;
        valid: boolean;
        remainingTime: number;
        vipStatus: boolean;
        type: PayCardType;
    };
}

interface AuthInfo {
    data: {
        modelStatus: number;
        appMemberStatus?: string;
    };
}

interface TopBarInfo {
    errno: number;
    errmsg: string;
    logid: string;
    data?: TopBarData;
}

export interface TopBarData {
    actId: string;
    content: {
        text: string;
        underlineText?: string;
        color?: string;
    };
    background?: {
        color?: string;
        img?: string;
        imgHeight?: number;
        imgWidth?: number;
        borderColor?: string;
        closeImg?: string;
    };
    scheme: string;
    beginTime: number;
    endTime: number;
    hideType: number;
}

// 0: "纯英文",1: "日",2: "韩",3: "中",4: "阿拉伯，维语",5: "印地语",6: "泰语",7: "藏语",8: "忽略",9:"非纯英文的其他西语语种"
// 多模返回值
export const enum LaguageType {
    EN = 0,
    JP = 1,
    KR = 2,
    CN = 3,
    ARABIC = 4,
    HINDI = 5,
    THAI = 6,
    TAMIL = 7,
    IGNORE = 8,
    OTHER_ENGLISH = 9
}

// 用于传值给 Server
// 映射表： https://fanyi-api.baidu.com/doc/21
export const enum LanguageCode {
    CN = 'zh',
}

interface LanguageResponseData {
    langTypeMap: LaguageType[];
}

export const likeAction = (params: LikeParams, api = FEEDBACK_API, isJson2query = true): Promise<boolean> => {
    const sendData = {
        ...params,
    };
    return httpPost<LikeResponse>(api, sendData, {
        timeout: 5000,
    }, isJson2query)
        .then(res => +res.data.errno === 0)
        .catch(e => {
            console.error(e);
            return false;
        });
};

export const submitFeedback = (params: {[propName: string]: string | number}) => {
    return httpPost<LikeResponse>(FEEDBACK_SUBMIT_API, params, {}, false, {
        'Content-Type': 'application/json',
    }).then(res => res.data).catch(() => ({}));
};

export const collectCharacter = (characterId: number, type: 1 | 2, operate: 1 | 2) => {
    const sendData = {
        characterId,
        type,
        operate,
    };

    return new Promise((resolve, reject) => {
        httpPost<LikeResponse>(COLLECT_CHARACTER_API, sendData, {
            timeout: 5000,
        }, false).then(res => {
            resolve(res.data);
        }).catch(err => {
            console.error(err);
            reject();
        });
    });
};

export const getMusicLink = (params: {[propName: string]: string | boolean | number}) => {
    const API = `${GET_MUSIC_LINK}?${buildParam(params)}`;
    return httpGet({
        url: API,
    }).then(res => res.data).catch(() => ({}));
};

export const getHistoryData = (params: {[propName: string]: string | boolean | number},
    api: string, abortController: AbortController) => {
    const API = `${api}?${buildParam(params)}`;
    // return historyData;
    return httpGet<HistoryResponse>({
        url: API,
        abortController,
    }).then(res => res.data);
};

export const createShare = (data: {
    idList: string;
    watermark: string;
    isVip?: boolean;
    uk?: string;
}) => {
    const {idList, watermark, isVip = false, uk} = data || {};
    const sendData = {
        idList,
        watermark,
        vp: isVip ? 1 : 0,
        ...uk && {
            uk,
        },
    };

    return new Promise((resolve, reject) => {
        httpPost<LikeResponse>(CHAT_SHARE_CREATE, sendData, {
            timeout: 5000,
        }, false).then(res => {
            resolve(res.data);
        }).catch(err => {
            console.error(err);
            reject();
        });
    });
};

export const postStopAnswer = (params: {[propName: string]: string | number}) => {
    return httpPost(STOP_ANSWER_API, params);
};

export const postConsume = (params: {[propName: string]: string}) => {
    return httpPost(CONSUME_API, params);
};

export const getCharacterInfo = (params: {[propName: string]: string}, abortController: AbortController) => {
    const API = `${CHARACTER_INFO}?${buildParam(params)}`;

    return httpGet({
        url: API,
        abortController,
    }).then(res => res.data);
};
export const getAdditionalInfo = (params: {[propName: string]: string | boolean}, abortController: AbortController) => {
    if (params?.isSkip) {
        return {};
    }
    const API = `${ADDITIONAL_INFO}?${buildParam(params)}`;
    return httpGet({
        url: API,
        abortController,
    }).then(res => res.data).catch(() => {
        return {};
    });
};

export const getCoinTotal = (params?: {
    from?: string;
    characterId?: number;
    promptId?: number;
}) => {
    const {from, characterId, promptId} = params || {};
    const payload = {
        timestamp: Date.now(),
        ...from && {
            from,
        },
        ...characterId && {
            characterId,
        },
        ...promptId && {
            promptId,
        },
    };
    return httpPost<CoinTotalRes>(COIN_TOTAL_API, payload, {}, false).then(res => {
        if (res.status === 200 && res.data.errno === 0) {
            return res.data.data;
        }
        return {
            totalCoin: 0,
            showNewGuide: false,
        };
    }).catch(() => {
        return {
            totalCoin: 0,
            showNewGuide: false,
        };
    });
};

export const postCoinTaskAdd = async () => {
    return httpPost<CoinTotalRes>(COIN_TASK_ADD_API, {
        type: 'guide',
    }, {}, false).then(res => {
        if (res.status === 200 && res.data.errno === 0) {
            return true;
        }
        return false;
    }).catch(() => false);
};

export const changeGuideStatus = async () => {
    return httpPost<CoinTotalRes>(COIN_GUIDE_STATUS, {}, {}, false)
        .then(res => {
            if (res.status === 200 && res.data.errno === 0) {
                return true;
            }
            return false;
        })
        .catch(() => false);
};

export const vipRefund = (characterId: number) => {
    const payload = {
        characterId,
    };
    return httpPost<CoinTotalRes>(COIN_VIP_REFUND_API, payload, {}, false).then(res => {
        if (res.status === 200 && res.data.errno === 0) {
            return true;
        }
        return false;
    }).catch(() => false);
};

export const getMapPluginRoutesData = (params: {[key: string]: string}) => {
    const API = `${HISTORY_API}/ext?${buildParam(params)}`;

    return httpGet({
        url: API,
    }).then(res => res.data);
};

export const getCoinDayPayCountdown = (params: { promptId: string } | { characterId: string }) => {
    return httpPost<CoinDayPay>(COIN_DAY_PAY_COUNTDOWN_API, params, {}, false).then(res => res.data.data);
};

export const getPromptInfo = (params: {[key: string]: string}, abortController: AbortController) => {
    const API = `${PROMPT_INFO}?${buildParam(params)}`;
    return httpGet({
        url: API,
        abortController,
    }).then(res => res.data);
};

export const getCouponCode = (params: any) => {
    return new Promise((resolve, reject) => {
        httpPost<Response>(GET_COUPON_CODE, params, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => {
            resolve(res.data);
        }).catch(err => {
            console.error(err);
            showNaToast(ERROR_NET);
            reject();
        });
    });
};

export const getAuthInfo = () => {
    return httpGet<AuthInfo>({
        url: AUTH_CHECK_API,
    }).then(res => res?.data?.data || {});
};

export const getSuggestions = (params: {[propName: string]: string | number},
    abortController: AbortController) => {
    const API = `${SUGGESTIONS_API}?${buildParam(params)}`;

    return httpGet({
        url: API,
        abortController,
    }).then(res => res.data).catch(err => {
        console.error(err);
    });
};

export const postChangeQuestion = (
    params: {[key: string]: any},
    abortController: AbortController
) => {
    return new Promise((resolve, reject) => {
        httpPost(
            QUESTION_API,
            params,
            {
                signal: abortController?.signal,
            },
            false
        ).then((res: any) => {
            if (res?.data?.errno) {
                console.error(res);
                reject(res);
            }
            else {
                resolve(res.data);
            }
        }).catch(err => {
            console.error(err);
            reject(err);
        });
    });
};

export const getFeedbackInfo = (params: {[propName: string]: string | boolean | number}) => {
    const API = `${FEEDBACKINFO_API}?${buildParam(params)}`;
    return httpGet({
        url: API,
    }).then(res => res.data).catch(() => ({}));
};
// 欢迎语落pb
export const postWelcomePb = (params: {[key: string]: string}) => {
    return httpPost(UPLOAD_API, params, {}, false).catch(err => {
        console.error(err);
    });
};

export const postShakeWelcome = (params: {[key: string]: string | number}): any => {
    return new Promise((resolve, reject) => {
        httpPost(SHAKE_API, params, {}, false).then((res: any) => {
            if (+res?.data?.errno === 0) {
                resolve(res.data);
            }
            else {
                reject();
            }
        }).catch((e: any) => {
            console.error(e);
            reject();
        });
    });
    // httpGet({
    //     url: SHAKE_API,
    // }).then(res => res.data).catch(() => ({}));
};

export const getFreeSubsCard = async (): Promise<FreeSubsCardRes['data'] | null> => {
    try {
        const res = await httpGet<FreeSubsCardRes>({
            url: FREE_SUBS_CARD,
        });
        if (+res?.data?.errno === 0) {
            return res.data.data;
        }
        return null;
    }
    catch {
        return null;
    }
};

export const postNps = (url: string, params: {[key: string]: any}) => {
    return new Promise(resolve => {
        httpPost<{ errno: number; errmsg: string; id: number }>(url, params, {
            /**
             * httpPost默认带有Acs-Token，请求UFO_API带上Acs-Token会被浏览器拦截
             * 通过options来将header中默认添加的Acs-Token去除
             */
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
                'referer': 'https://www.baidu.com',
            },
        }, true).then((res: any) => {
            resolve(res.data);
        }).catch(err => {
            console.error(err);
        });
    });
};

export const getShortUrl = (url: string) => {
    return new Promise(resolve => {
        httpPost(SHORTENURL_API, {
            url,
        }, {}, false).then((res: any) => {
            if (+res?.data?.errno === 0) {
                resolve(res.data.data.url);
            }
            else {
                resolve(url);
            }
        }).catch((e: any) => {
            console.error(e);
            resolve(url);
        });
    });
};

export const getLanguage = (url: string): Promise<LaguageType[]> => {
    const languages = [] as unknown as LanguageResponseData['langTypeMap'];
    return new Promise(resolve => {
        httpPost(GET_LANGUAGE, {
            url,
        }, {}, false).then((res: any) => {
            if (+res?.data?.errno === 0) {
                resolve(res.data?.data?.langTypeMap as LanguageResponseData['langTypeMap']);
            }
            else {
                resolve(languages);
            }
        }).catch(() => {
            resolve(languages);
        });
    });
};
export const gainBenefitsCard = (params: {
    actId: number;
    inviterUk: string;
    cardFrom?: string;
    [key: string]: any;
}) => {
    return new Promise<LikeResponse>((resolve, reject) => {
        window.__commonParams?.split('&').forEach(param => {
            const [
                key,
                value,
            ] = param.split('=');
            params[key] = value;
        });
        httpPost<LikeResponse>(BENEFITSCARDGAIN_API, params)
            .then(res => resolve(res.data))
            .catch(err => reject(err));
    });
};

interface ResumeResponse {
    data: AnsResponse;
    errmsg: string;
    errno: number;
    logid: string;
}
export const getRecoverData = (params: {
    messageId: string;
    askContent?: string;
    askMId?: string;
    serverToken?: string;
}) => {
    return new Promise<ResumeResponse>((resolve, reject) => {
        httpPost<ResumeResponse>(CHAT_RECOVER_API, params)
            .then(res => resolve(res.data))
            .catch(err => reject(err));
    });
};

// 欢迎语进历史
export const welcomeInHistory = (params: {
    characterId?: number;
    promptId?: number;
    botId: number;
    type: WelcomeCardType;
    data: any;
    sessionType?: string;
    from?: string;
}, abortController: AbortController) => {
    return new Promise((resolve, reject) => {
        httpPost(WELCOME_IN_HISTORY_API, {
            ...params,
        }, {
            signal: abortController?.signal,
        }, false, {
            'Content-Type': 'application/json',
        }).then(res => resolve(res.data)).catch(err => reject(err));
    });
};
// 更新欢迎语历史数据
export const updateWelcomeHistoryData = (params: {
    boxId: string;
    data: any;
}) => {
    return new Promise((resolve, reject) => {
        httpPost(UPDATE_WELCOME_HISTORY_API, {
            ...params,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => resolve(res.data)).catch(err => reject(err));
    });
};

// 主动推荐卡点击上报server
export const welcomeConsumed = (params: {
    characterId?: number;
    promptId?: number;
    wIdStr: string;
    personId: number;
    isHistory?: number;
    isShake?: number;
    botId?: number;
    boxId?: string | number;
    type?: WelcomeCardType;
    sessionType?: string;
}) => {
    return new Promise((resolve, reject) => {
        httpPost(WELCOME_CONSUMED_API, {
            ...params,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => resolve(res.data)).catch(err => reject(err));
    });
};

// 获取异步主动推荐卡
export const welcomeAsync = (params: {
    charId?: number;
    personId?: number;
    asyncVideoMeta?: Record<string, string | number>;
    promptId?: number;
    wIdStr?: string;
    isHistory?: number;
    isShake?: number;
    botId?: number;
    type?: WelcomeCardType;
    sessionType?: string;
}, abortController: AbortController) => {
    return new Promise((resolve, reject) => {
        httpPost(WELCOME_ASYNC_API, {
            ...params,
        }, {
            signal: abortController?.signal,
        }, false, {
            'Content-Type': 'application/json',
        }).then(res => resolve(res.data)).catch(err => reject(err));
    });
};

// 点击二级会话页进智能体详情之后上报
export const reportVisitAgent = (params: {
    id: string;
    type: string;
}) => {
    return new Promise((resolve, reject) => {
        httpPost(REPORT_VISIT_AGENT, {
            ...params,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => resolve(res.data)).catch(err => reject(err));
    });
};

// 线索卡提交接口
export const submitClue = (clue: any) => {
    return new Promise((resolve, reject) => {
        httpPost<{errno: number; data: {successfulText: string}}>(POST_CLUE_API, {
            ...clue,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => {
            const errno = res.data?.errno;
            switch (errno) {
                case 0:
                    resolve(res.data?.data.successfulText);
                    break;
                case 623:
                    reject('验证码错误');
                    break;
                case 627:
                    reject('验证码已过期，请重新获取');
                    break;
                default:
                    reject('网络不给力，请稍候重试');
            }
        }).catch(() => reject('网络不给力，请稍候重试'));
    });
};

// 线索卡获取验证码
export const getSMSCode = (phone: string) => {
    return new Promise((resolve, reject) => {
        httpPost<{errno: number; errmsg: string; data: string}>(SMS_CODE_API, {
            phone,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => {
            if (res.status === 200) {
                res.data?.errno === 0 && resolve(res.data.data);
                if ([
                    624, // 验证码仍在有效期内
                    625, // 您今日的验证码发送已达上限
                ].includes(res.data?.errno)) {
                    reject(res.data?.errmsg);
                }
            }
            else {
                reject('验证码发送失败');
            }
        }).catch(() => {
            reject('验证码发送失败');
        });
    });
};

/**
 * 早下班任务完成上报
 */
export const postOffworkReport = () => {
    return new Promise<any>((resolve, reject) => {
        httpPost<{errno: number; errmsg: string; data: string}>(OFF_WORK_REPORT_API, {})
            .then(res => resolve(res.data))
            .catch(err => reject(err));
    });
};

// 智能体框上引导信息
export const getInputBoxGuide = (params: { characterId: string}) => {
    const API = `${INPUT_BOX_GUIDE_API}?${buildParam(params)}`;
    return httpGet<{errno: number; data: any}>({
        url: API,
    }).then(res => {
        if (res.data?.errno === 0) {
            return res.data?.data;
        }
    }).catch(() => ({}));
};

// 记忆时间修改
export const updateFreeSubscribe = (params: {
    memId: number;
    reminderId: number;
    text: string;
    period: string;
    time: string;
}) => {
    return new Promise((resolve, reject) => {
        httpPost<{errno: number; errmsg: string; data: string}>(FREE_SUBSCRIBE_UPDATE, {
            ...params,
        }, {}, false, {
            'Content-Type': 'application/json',
        }).then(res => {
            if (res.data?.errno === 0) {
                return resolve(res.data?.data);
            }
            reject('网络不给力，请稍候重试');

        }).catch(() => reject('网络不给力，请稍候重试'));
    });
};

interface MessageDeleteParams {
    botId: string;
    typeId: string;
    msgs?: DeleteMessageData[];
    boxes?: DeleteMessageData[];
}

interface BotDeleteParams {
    botId: string;
    type?: number;
}

// 清空会话，删除bot
export const postBotDelete = (params: BotDeleteParams) => {
    return new Promise((resolve, reject) => {
        httpPost(DELETE_BOT_API, params, {}, false)
            .then(res => resolve(res.data)).catch(err => reject(err));
    });
};

// 删除会话
export const postMessageDelete = (params: MessageDeleteParams) => {
    return new Promise((resolve, reject) => {
        httpPost(DELETE_MESSAGE_API, params, {}, false)
            .then(res => resolve(res.data)).catch(err => reject(err));
    });
};

export const getTopBar = () => {
    return httpGet<TopBarInfo>({
        url: TOP_BAR_API,
    }).then(res => res?.data?.data || {}).catch(() => ({}));
};

export const generateDocument = (params: {
    messageIds: string[];
    htmlContent: string;
    sign: string;
    convertType: number;
    ts: number;
}) => {
    return new Promise(resolve => {
        httpPost<{
            errno: number;
            errmsg: string;
            logid: string;
            data?: {
                fileName: string;
                fileType: string;
                fileUrl: string;
            };
        }>(GENERATE_DOCUMENT_API, params, {}, false)
            .then(res => {
                if (res.data?.errno === 0) {
                    return resolve(res.data?.data);
                }
                resolve(res.data || {});
            })
            .catch(() => {
                showNaToast(ERROR_NET);
                resolve({});
            }
            );
    });
};

interface ExploreWorldSug {
    id: number;
    url: string;
}
export const postExploreWorldSug = (
    params: ExploreWorldSug,
    abortController?: AbortController
) => {
    return httpPost<LikeResponse>(EXPLORE_WORLD_SUG_API, params, {
        signal: abortController?.signal,
        timeout: 50000,
    }, false)
        .then(res => res?.data || {})
        .catch(e => ({error: e}));
};
